import React from 'react';
import { cn } from './cn';

export type ContainerSize = 'sm' | 'md' | 'lg' | 'xl';

// sm/md: formularios y configuración (angostos a propósito, se leen mejor).
// lg/xl: páginas de datos — ocupan todo el ancho del <main>, igual que el
// Panel principal y Mi Ganado (no desperdiciar los costados en 1080p/2K).
const maxSizes: Record<ContainerSize, string> = {
  sm: 'max-w-3xl',
  md: 'max-w-5xl',
  lg: 'max-w-none',
  xl: 'max-w-none',
};

export interface PageContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: ContainerSize;
}

/**
 * Contenedor único de página: reemplaza el caos actual de max-w-3xl/4xl/5xl/7xl
 * y container-fluid inerte por un ancho consistente.
 * El padding lo pone el <main> del Layout (p-2 … lg:p-8); acá solo se suma un
 * poco de aire lateral en mobile para no duplicar márgenes en desktop.
 */
export const PageContainer: React.FC<PageContainerProps> = ({ size = 'lg', className, children, ...rest }) => (
  <div className={cn('mx-auto w-full px-2 sm:px-0', maxSizes[size], className)} {...rest}>
    {children}
  </div>
);

export interface PageHeadingProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Eyebrow / contexto encima del título (ej: nombre del módulo). */
  eyebrow?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}

/**
 * Encabezado de página con tipografía display (Onest), subtítulo en salvia
 * y slot de acciones a la derecha. Reemplaza los h1 ad-hoc de las páginas.
 */
export const PageHeading: React.FC<PageHeadingProps> = ({ title, subtitle, eyebrow, actions, className }) => (
  <div className={cn('mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between', className)}>
    <div className="min-w-0">
      {eyebrow && <div className="mb-1 text-xs font-semibold uppercase tracking-[0.16em] text-forest-mid">{eyebrow}</div>}
      <h1 className="font-display text-2xl font-semibold leading-tight text-ink sm:text-3xl">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-sage">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

export default PageContainer;
